import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { CHARGEPOINT_MIN_CURRENT } from '../../../common/domain/electrical-settings'
import { ChargePoint, Connector, useLoadBalancerGroupGetByIdQuery } from '../../../common/redux/api/web-api'
import { isAdminDomain, isSupportDomain } from '../../../lib/currentDomain'
import useText from '../../../common/hooks/useText'
import { ComboboxOption, ComboboxValue } from '../../../common/components/combobox'
import { Input } from '../../../common/uikit/Input'
import { Dropdown } from '../../../common/uikit/Dropdown'

export type OcppConnectorLoadbalancerSettingsProps = {
    connector: Connector
    chargePoint: ChargePoint
    isOnline?: boolean
    isLocked: boolean
    phasesOptions?: ComboboxOption<ComboboxValue>[]
}

const getErrorMessage = (error: unknown): string | undefined => {
    if (!error || typeof error !== 'object' || !('message' in error)) return
    return typeof error.message === 'string' ? error.message : undefined
}

export function OcppConnectorLoadbalancerSettings({
    connector,
    chargePoint,
    isOnline,
    isLocked,
    phasesOptions,
}: OcppConnectorLoadbalancerSettingsProps): React.ReactElement | null {
    const text = useText()
    const { data: loadbalancerGroup } = useLoadBalancerGroupGetByIdQuery(
        { id: chargePoint.loadBalancerGroupId ?? 0 },
        { skip: !chargePoint.loadBalancerGroupId }
    )

    const {
        watch,
        formState: { errors },
        control,
        register,
    } = useFormContext()

    const installationPhases = watch('installationPhases')

    if (!chargePoint.loadBalancerGroupId || !loadbalancerGroup) {
        return null
    }

    if (connector.connectorFunctionType === 'Schuko') {
        return null
    }

    const showOfflinePhase = Number(installationPhases) === 3

    return (
        <div data-testid="lbSettings">
            <Input
                disabled={
                    isLocked ||
                    loadbalancerGroup.active ||
                    isAdminDomain ||
                    !isOnline ||
                    (!isSupportDomain && chargePoint?.ocppEnabled)
                }
                disabledReason={loadbalancerGroup.active ? 'msgChargerInActiveLBGroup' : undefined}
                type="number"
                min={connector.minCurrent ?? CHARGEPOINT_MIN_CURRENT}
                label={text('lblLoadBalancerOfflineCurrentLimitField')}
                infoText={text('msgLoadBalancerOfflineCurrentLimitField')}
                id="offlineCurrent-value"
                data-testid={`lbOfflineCurrent_${connector.connectorId}-input`}
                {...register(`lbc_${connector.connectorId}`)}
                error={getErrorMessage(errors[`lbc_${connector.connectorId}`])}
            />

            {showOfflinePhase && (
                <div className="mt-5 mb-2" data-testid="offlinePhase">
                    <Controller
                        name={`lbOfflinePhase_${connector.connectorId}`}
                        control={control}
                        render={({ field: { value, onChange } }) => {
                            return (
                                <Dropdown
                                    options={phasesOptions}
                                    disabled={isLocked || loadbalancerGroup.active || isAdminDomain}
                                    disabledReason={
                                        loadbalancerGroup.active ? text('msgChargerInActiveLBGroup') : undefined
                                    }
                                    label={text('lblLoadBalancerOfflinePhase')}
                                    infoText={text('msgLoadBalancerOfflinePhase')}
                                    showClear={false}
                                    id="offlinePhaseValue"
                                    data-testid={`lbOfflinePhase_${connector.connectorId}-select`}
                                    value={value}
                                    onChange={(option) => {
                                        onChange(option.value)
                                    }}
                                    error={getErrorMessage(errors[`lbOfflinePhase_${connector.connectorId}`])}
                                />
                            )
                        }}
                    />
                </div>
            )}
        </div>
    )
}
